import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppStore } from "@/store/store-hooks";
import { useBoxControlsClippingPlanes } from "@/utils/box-controls-context";
import { planesToArray } from "@/utils/planes-to-array";
import { sanitizeFileName } from "@faro-lotv/foundation";
import {
  selectIElementWorldMatrix,
  selectProjectId,
  selectProjectName,
} from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback } from "react";
import { Matrix4 } from "three";
import { IPX_EXPORT_FORMAT } from "./ipx-config";

/** @returns a function starting the export of the region sent to IPX, tagged with the IPX order it belongs to */
export function useStartIpxExport(): (orderId: string) => Promise<void> {
  const { pointCloudApiClient, coreApiClient } = useApiClientContext();
  const { getState } = useAppStore();
  const { handleErrorWithToast } = useErrorHandlers();
  const clippingBoxPlanes = useBoxControlsClippingPlanes();

  return useCallback(
    async (orderId: string) => {
      const state = getState();
      const projectId = selectProjectId(state);
      const { pointCloudId, selectedScanIds } = state.ipxOrderMode;
      try {
        if (!projectId || !pointCloudId || !clippingBoxPlanes) {
          throw new Error("The region to export is not defined");
        }
        // Same conversion as the point cloud export of the Export mode
        const transformation = new Matrix4()
          .fromArray(selectIElementWorldMatrix(pointCloudId)(state))
          .premultiply(new Matrix4().makeRotationX(Math.PI / 2))
          .transpose()
          .toArray();
        const {
          data: { userId },
        } = await coreApiClient.getLoggedInUser();
        await pointCloudApiClient.exportSubVolume({
          userId,
          projectId,
          pointCloudId,
          format: IPX_EXPORT_FORMAT,
          fileName: sanitizeFileName(selectProjectName(state) ?? "ipx-export"),
          boundingBox: planesToArray(clippingBoxPlanes),
          transformation,
          // No scans selected means the whole point cloud, the backend rejects an empty list
          scanIds: selectedScanIds.length ? selectedScanIds : undefined,
          tags: [`ipx-${orderId}`],
        });
      } catch (error) {
        handleErrorWithToast({
          title: "The data for IPX could not be exported",
          error,
        });
      }
    },
    [
      clippingBoxPlanes,
      coreApiClient,
      getState,
      handleErrorWithToast,
      pointCloudApiClient,
    ],
  );
}
